/**
 * Offline ZzFX synth: a faithful port of `ZZFX.buildSamples` from ZzFX 1.4.0 (MIT, (c) 2019 Frank Force,
 * https://github.com/KilledByAPixel/ZzFX).
 *
 * Why a port instead of `import { ZZFX } from 'zzfx'`: the package creates an AudioContext at module load (an
 * autoplay-policy warning before the first gesture, and a crash in Node tests). This port renders into a
 * Float32Array with an explicit sample rate, has no global state, and is deterministic: the per-play frequency
 * `randomness` (param 1) is ignored here — the engine applies pitch variation at playback via `playbackRate`, the
 * same way `ZZFXSound` does — so a bank sound is rendered once and cached as an AudioBuffer.
 *
 * Parameter order (all optional, `undefined` = default):
 *   0 volume=1, 1 randomness=.05 (ignored), 2 frequency=220, 3 attack=0, 4 sustain=0, 5 release=.1,
 *   6 shape=0 (0 sin, 1 tri, 2 saw, 3 tan, 4 noise, 5 square), 7 shapeCurve=1, 8 slide=0, 9 deltaSlide=0,
 *   10 pitchJump=0, 11 pitchJumpTime=0, 12 repeatTime=0, 13 noise=0, 14 modulation=0, 15 bitCrush=0, 16 delay=0,
 *   17 sustainVolume=1, 18 decay=0, 19 tremolo=0, 20 filter=0 (>0 high-pass, <0 low-pass; cutoff ≈ 2·|filter| Hz)
 */
export type ZzfxParams = readonly (number | undefined)[];

const PI2 = Math.PI * 2;
const sign = (v: number): number => (v < 0 ? -1 : 1);

/** Render one ZzFX sound. Returns a new Float32Array (length = attack + decay + sustain + release + delay). */
export const buildZzfxSamples = (params: ZzfxParams, sampleRate = 44100): Float32Array => {
  const p = (i: number, d: number): number => {
    const v = params[i];
    return v === undefined || Number.isNaN(v) ? d : v;
  };
  const volume = p(0, 1);
  let frequency = p(2, 220);
  let attack = p(3, 0);
  let sustain = p(4, 0);
  let release = p(5, 0.1);
  const shape = p(6, 0);
  const shapeCurve = p(7, 1);
  let slide = p(8, 0);
  let deltaSlide = p(9, 0);
  let pitchJump = p(10, 0);
  let pitchJumpTime = p(11, 0);
  let repeatTime = p(12, 0);
  const noise = p(13, 0);
  let modulation = p(14, 0);
  const bitCrush = p(15, 0);
  let delay = p(16, 0);
  const sustainVolume = p(17, 1);
  let decay = p(18, 0);
  const tremolo = p(19, 0);
  const filter = p(20, 0);

  const startSlide = (slide *= (500 * PI2) / sampleRate / sampleRate);
  let startFrequency = (frequency *= PI2 / sampleRate);
  let modOffset = 0;
  let repeat = 0;
  let crush = 0;
  let jump = 1;
  let t = 0;
  let i = 0;
  let s = 0;
  let f: number;

  // biquad LP/HP filter (RBJ), as in ZzFX
  const quality = 2;
  const w = (PI2 * Math.abs(filter) * 2) / sampleRate;
  const cos = Math.cos(w);
  const alpha = Math.sin(w) / 2 / quality;
  const a0 = 1 + alpha;
  const a1 = (-2 * cos) / a0;
  const a2 = (1 - alpha) / a0;
  const b0 = (1 + sign(filter) * cos) / 2 / a0;
  const b1 = -(sign(filter) + cos) / a0;
  const b2 = b0;
  let x2 = 0;
  let x1 = 0;
  let y2 = 0;
  let y1 = 0;

  const minAttack = 9; // prevent a pop when attack is 0
  attack = attack * sampleRate || minAttack;
  decay *= sampleRate;
  sustain *= sampleRate;
  release *= sampleRate;
  delay *= sampleRate;
  deltaSlide *= (500 * PI2) / sampleRate ** 3;
  modulation *= PI2 / sampleRate;
  pitchJump *= PI2 / sampleRate;
  pitchJumpTime *= sampleRate;
  repeatTime = (repeatTime * sampleRate) | 0;

  const length = (attack + decay + sustain + release + delay) | 0;
  const b = new Float32Array(length > 0 ? length : 0);
  const crushEvery = (bitCrush * 100) | 0;

  for (; i < length; b[i++] = s * volume) {
    if (!(++crush % crushEvery)) {
      s = shape
        ? shape > 1
          ? shape > 2
            ? shape > 3
              ? shape > 4
                ? (t / PI2) % 1 < shapeCurve / 2
                  ? 1
                  : -1 // 5 square duty
                : Math.sin(t ** 3) // 4 noise
              : Math.max(Math.min(Math.tan(t), 1), -1) // 3 tan
            : 1 - (((((2 * t) / PI2) % 2) + 2) % 2) // 2 saw
          : 1 - 4 * Math.abs(Math.round(t / PI2) - t / PI2) // 1 triangle
        : Math.sin(t); // 0 sin

      s =
        (repeatTime ? 1 - tremolo + tremolo * Math.sin((PI2 * i) / repeatTime) : 1) *
        (shape > 4 ? s : sign(s) * Math.abs(s) ** shapeCurve) *
        (i < attack
          ? i / attack
          : i < attack + decay
            ? 1 - ((i - attack) / decay) * (1 - sustainVolume)
            : i < attack + decay + sustain
              ? sustainVolume
              : i < length - delay
                ? ((length - i - delay) / release) * sustainVolume
                : 0);

      s = delay
        ? s / 2 +
          (delay > i
            ? 0
            : ((i < length - delay ? 1 : (length - i) / delay) * (b[(i - delay) | 0] ?? 0)) / 2 / volume)
        : s;

      if (filter) {
        const y = b2 * x2 + b1 * x1 + b0 * s - a2 * y2 - a1 * y1;
        x2 = x1;
        x1 = s;
        y2 = y1;
        y1 = y;
        s = y;
      }
    }

    f = (frequency += slide += deltaSlide) * Math.cos(modulation * modOffset++);
    t += f + f * noise * (((i * i * PI2) % 2) - 1);

    if (jump && ++jump > pitchJumpTime) {
      frequency += pitchJump;
      startFrequency += pitchJump;
      jump = 0;
    }

    if (repeatTime && !(++repeat % repeatTime)) {
      frequency = startFrequency;
      slide = startSlide;
      jump ||= 1;
    }
  }
  return b;
};
